import {
  ComponentProps,
  createContext,
  useContext,
  useId,
  useState,
} from "react";

export interface RootProps extends ComponentProps<"div"> {
  onSelectedFiles: (files: File[]) => void;
  fileStatus?: string[];
}

type FileInputContextType = {
  id: string;
  files: File[];
  onFilesSelected: (files: File[], multiple: boolean) => void;
  onDeleted: (index: number) => void;
  onSetInputRef: (ref: React.RefObject<HTMLInputElement>) => void;
  fileStatus?: string[];
};

const FileInputContext = createContext({} as FileInputContextType);

export function Root(props: RootProps) {
  const id = useId();
  const [files, setFiles] = useState<File[]>([]);
  const [inputRef, setInputRef] = useState<React.Ref<HTMLInputElement>>(null);
  const { onSelectedFiles, fileStatus, ...restProps } = props;

  function onFilesSelected(file: File[], multiple: boolean) {
    if (multiple) {
      const combinedFiles = [...files, ...file];
      setFiles(combinedFiles);
      onSelectedFiles(combinedFiles);
    } else {
      setFiles(file);
      onSelectedFiles(file);
    }
  }

  function onDeleted(index: number) {
    onSelectedFiles(files.filter((_, i) => i !== index));
    setFiles((state) => state.filter((_, i) => i !== index));
    if (
      inputRef &&
      typeof inputRef !== "function" &&
      "current" in inputRef &&
      inputRef.current
    ) {
      inputRef.current.value = "";
    }
  }

  function onSetInputRef(ref: React.RefObject<HTMLInputElement>) {
    setInputRef(ref);
  }

  return (
    <FileInputContext.Provider
      value={{
        id,
        files,
        onFilesSelected,
        onDeleted,
        onSetInputRef,
        fileStatus,
      }}
    >
      <div {...restProps} />
    </FileInputContext.Provider>
  );
}

export const useFileInput = () => useContext(FileInputContext);
